import type {
  Breakpoint,
  Column,
  Page,
  PageMeta,
  Plan,
  Section,
  Theme,
  Widget,
} from "@emkit/builder-core";
import { temporal } from "zundo";
import { create, useStore } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type EditorMode = "edit" | "preview";

/**
 * A selection points at exactly one Widget, Column, or Section. All
 * inspectors branch on `kind` to choose their UI.
 */
export type Selection =
  | { kind: "widget"; id: string }
  | { kind: "section"; id: string }
  | { kind: "column"; id: string }
  | null;

interface EditorState {
  page: Page;
  selection: Selection;
  mode: EditorMode;
  /** Active breakpoint — drives canvas rendering AND responsive field edits. */
  breakpoint: Breakpoint;
  isGalleryOpen: boolean;
  isPageSettingsOpen: boolean;
  isAISettingsOpen: boolean;
  /**
   * Active subscription plan. Drives palette lock badges and what gets
   * sent to the AI in the system prompt. Defaults to "free" so a host
   * that hasn't set a plan keeps users on the safest tier.
   */
  plan: Plan;
  setPlan: (plan: Plan) => void;
  /** In-memory clipboard for copy/paste widgets across sections. */
  clipboardWidget: Widget | null;

  setPage: (page: Page) => void;
  setMode: (mode: EditorMode) => void;
  setBreakpoint: (bp: Breakpoint) => void;
  /** Patch page-level metadata (title, slug, meta). Goes through history. */
  updatePageMeta: (patch: {
    title?: string;
    slug?: string;
    meta?: PageMeta;
  }) => void;
  /**
   * Merge into page.theme. Empty/undefined fields drop their current value
   * so the user can clear a token via the UI without setting a literal "".
   */
  updatePageTheme: (patch: Partial<Theme>) => void;

  selectWidget: (id: string | null) => void;
  selectSection: (id: string | null) => void;
  selectColumn: (id: string | null) => void;
  clearSelection: () => void;

  /** Copy selected (or given) widget to in-memory clipboard. */
  copyWidget: (id?: string) => void;
  /** Paste clipboard widget after the current selection, or into the
   *  first column if nothing useful is selected. Returns true on success. */
  pasteWidget: () => boolean;

  openGallery: () => void;
  closeGallery: () => void;
  openPageSettings: () => void;
  closePageSettings: () => void;
  openAISettings: () => void;
  closeAISettings: () => void;

  // Widget operations
  updateWidgetProps: (id: string, patch: Record<string, unknown>) => void;
  /**
   * Patch top-level Widget fields (className/style/anchor/hideOn — everything
   * outside of `props`). Use this for the shared CustomAttributes panel; for
   * type-specific props use updateWidgetProps.
   */
  updateWidget: (
    id: string,
    patch: Partial<Omit<Widget, "id" | "type" | "props">>,
  ) => void;
  deleteWidget: (id: string) => void;
  duplicateWidget: (id: string) => void;
  moveWidget: (id: string, direction: "up" | "down") => void;
  appendWidget: (widget: Widget) => void;
  /**
   * Replace an existing widget (matched by `id`) with `next` in place.
   * Used by the AI prompt box's "selection" mode where the model rewrites
   * the selected widget. The replacement keeps the original position
   * within its column.
   */
  replaceWidget: (id: string, next: Widget) => void;
  insertWidget: (columnId: string, index: number, widget: Widget) => void;
  reorderWithinColumn: (columnId: string, fromIndex: number, toIndex: number) => void;
  moveWidgetBetweenColumns: (
    widgetId: string,
    fromColumnId: string,
    toColumnId: string,
    toIndex: number,
  ) => void;

  // Section operations
  updateSection: (id: string, patch: Partial<Omit<Section, "id" | "columns">>) => void;
  addSection: (afterSectionId?: string) => void;
  removeSection: (id: string) => void;
  moveSection: (id: string, direction: "up" | "down") => void;
  duplicateSection: (id: string) => void;

  // Column operations
  updateColumn: (id: string, patch: Partial<Omit<Column, "id" | "widgets">>) => void;
  addColumn: (sectionId: string, afterColumnId?: string) => void;
  removeColumn: (sectionId: string, columnId: string) => void;
  moveColumn: (sectionId: string, columnId: string, direction: "left" | "right") => void;
  duplicateColumn: (sectionId: string, columnId: string) => void;
  /**
   * Replace a section's columns with N columns of the given spans.
   * Widgets migrate by column index; trailing widgets spill into the
   * last kept column when shrinking, so nothing is lost.
   */
  setColumnLayout: (sectionId: string, spans: number[]) => void;

  // Smart delete — dispatches on current selection kind.
  deleteSelected: () => void;

  resetPage: () => void;
}

// ---------- helpers ----------

function mapWidgetLists(page: Page, fn: (widgets: Widget[]) => Widget[]): Page {
  return {
    ...page,
    sections: page.sections.map((s) => ({
      ...s,
      columns: s.columns.map((c) => ({ ...c, widgets: fn(c.widgets) })),
    })),
  };
}

function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

function mapSections(page: Page, fn: (s: Section) => Section | null): Page {
  return {
    ...page,
    sections: page.sections.map(fn).filter((s): s is Section => s !== null),
  };
}

export const BLANK_PAGE: Page = {
  id: "blank",
  title: "Untitled",
  slug: "/",
  sections: [{ id: "s-default", columns: [{ id: "c-default", widgets: [] }] }],
};

export const PERSIST_KEY = "emkit.embuilder.editor.v1";

// ---------- store ----------

const useEditorBase = create<EditorState>()(
  temporal(
    persist(
      (set, get) => ({
        page: BLANK_PAGE,
        selection: null,
        mode: "edit",
        breakpoint: "desktop",
        isGalleryOpen: false,
        isPageSettingsOpen: false,
        isAISettingsOpen: false,
        plan: "free" as Plan,
        clipboardWidget: null,

        setPlan: (plan: Plan) => set({ plan }),

        setPage: (page) => set({ page, selection: null }),
        setMode: (mode) => set({ mode }),
        setBreakpoint: (bp) => set({ breakpoint: bp }),
        updatePageMeta: (patch) =>
          set((state) => ({
            page: {
              ...state.page,
              ...(patch.title !== undefined ? { title: patch.title } : {}),
              ...(patch.slug !== undefined ? { slug: patch.slug } : {}),
              ...(patch.meta !== undefined ? { meta: patch.meta } : {}),
            },
          })),

        updatePageTheme: (patch) =>
          set((state) => {
            const current = state.page.theme ?? {};
            const next: Theme = { ...current };
            // Merge colors specifically so token edits don't clobber siblings.
            if (patch.colors !== undefined) {
              next.colors = { ...(current.colors ?? {}), ...patch.colors };
            }
            for (const key of ["fontFamily", "headingFontFamily", "baseRadius"] as const) {
              if (patch[key] !== undefined) {
                if (patch[key] === "" || patch[key] === null) {
                  delete next[key];
                } else {
                  // biome-ignore lint/suspicious/noExplicitAny: narrowed by key loop
                  (next as any)[key] = patch[key];
                }
              }
            }
            // Drop the theme key entirely when nothing remains, keeping JSON tidy.
            const isEmpty =
              !next.colors &&
              !next.fontFamily &&
              !next.headingFontFamily &&
              next.baseRadius === undefined;
            return { page: { ...state.page, theme: isEmpty ? undefined : next } };
          }),

        selectWidget: (id) =>
          set({ selection: id === null ? null : { kind: "widget", id } }),
        selectSection: (id) =>
          set({ selection: id === null ? null : { kind: "section", id } }),
        selectColumn: (id) =>
          set({ selection: id === null ? null : { kind: "column", id } }),
        clearSelection: () => set({ selection: null }),

        openGallery: () => set({ isGalleryOpen: true }),
        closeGallery: () => set({ isGalleryOpen: false }),
        openPageSettings: () => set({ isPageSettingsOpen: true }),
        closePageSettings: () => set({ isPageSettingsOpen: false }),
        openAISettings: () => set({ isAISettingsOpen: true }),
        closeAISettings: () => set({ isAISettingsOpen: false }),

        copyWidget: (id) => {
          const state = get();
          const targetId =
            id ?? (state.selection?.kind === "widget" ? state.selection.id : null);
          if (!targetId) return;
          for (const s of state.page.sections) {
            for (const c of s.columns) {
              const w = c.widgets.find((w) => w.id === targetId);
              if (w) {
                // Store a deep-cloned snapshot so later edits of the
                // source widget don't mutate the clipboard copy.
                set({ clipboardWidget: structuredClone(w) });
                return;
              }
            }
          }
        },

        pasteWidget: () => {
          const state = get();
          const clip = state.clipboardWidget;
          if (!clip) return false;
          const fresh: Widget = {
            ...structuredClone(clip),
            id: newId(clip.type),
          };

          // Paste target resolution: selected widget → insert after it;
          // selected column → append; otherwise append to first column.
          if (state.selection?.kind === "widget") {
            const selId = state.selection.id;
            for (const s of state.page.sections) {
              for (const c of s.columns) {
                const idx = c.widgets.findIndex((w) => w.id === selId);
                if (idx !== -1) {
                  state.insertWidget(c.id, idx + 1, fresh);
                  return true;
                }
              }
            }
          }
          if (state.selection?.kind === "column") {
            const colId = state.selection.id;
            for (const s of state.page.sections) {
              const col = s.columns.find((c) => c.id === colId);
              if (col) {
                state.insertWidget(colId, col.widgets.length, fresh);
                return true;
              }
            }
          }
          state.appendWidget(fresh);
          return true;
        },

        // ---------- widget ----------

        updateWidgetProps: (id, patch) =>
          set((state) => ({
            page: mapWidgetLists(state.page, (widgets) =>
              widgets.map((w) =>
                w.id === id
                  ? {
                      ...w,
                      props: {
                        ...(w.props as Record<string, unknown>),
                        ...patch,
                      },
                    }
                  : w,
              ),
            ),
          })),

        updateWidget: (id, patch) =>
          set((state) => ({
            page: mapWidgetLists(state.page, (widgets) =>
              widgets.map((w) => (w.id === id ? { ...w, ...patch } : w)),
            ),
          })),

        deleteWidget: (id) =>
          set((state) => {
            const wasSelected =
              state.selection?.kind === "widget" && state.selection.id === id;
            return {
              page: mapWidgetLists(state.page, (widgets) =>
                widgets.filter((w) => w.id !== id),
              ),
              selection: wasSelected ? null : state.selection,
            };
          }),

        duplicateWidget: (id) =>
          set((state) => {
            let newWidgetId: string | null = null;
            const page = mapWidgetLists(state.page, (widgets) => {
              const idx = widgets.findIndex((w) => w.id === id);
              if (idx === -1) return widgets;
              const src = widgets[idx];
              if (!src) return widgets;
              const clone: Widget = {
                ...src,
                id: newId(src.type),
                props: structuredClone(src.props),
              };
              newWidgetId = clone.id;
              return [...widgets.slice(0, idx + 1), clone, ...widgets.slice(idx + 1)];
            });
            return {
              page,
              selection: newWidgetId
                ? { kind: "widget", id: newWidgetId }
                : state.selection,
            };
          }),

        moveWidget: (id, direction) =>
          set((state) => ({
            page: mapWidgetLists(state.page, (widgets) => {
              const idx = widgets.findIndex((w) => w.id === id);
              if (idx === -1) return widgets;
              const swap = direction === "up" ? idx - 1 : idx + 1;
              if (swap < 0 || swap >= widgets.length) return widgets;
              const next = widgets.slice();
              const a = next[idx];
              const b = next[swap];
              if (!a || !b) return widgets;
              next[idx] = b;
              next[swap] = a;
              return next;
            }),
          })),

        appendWidget: (widget) =>
          set((state) => {
            // If the page is empty (no sections, or first section has no
            // columns), create a default section/column to host the widget.
            // Without this branch the AI's "add a hero" on a blank page
            // would be a silent no-op.
            const sections =
              state.page.sections.length > 0 &&
              (state.page.sections[0]?.columns.length ?? 0) > 0
                ? state.page.sections
                : [
                    {
                      id: newId("s"),
                      columns: [{ id: newId("c"), widgets: [] }],
                      padding: { top: 64, right: 24, bottom: 64, left: 24 },
                    },
                    ...state.page.sections.slice(1),
                  ];
            return {
              page: {
                ...state.page,
                sections: sections.map((s, si) =>
                  si === 0
                    ? {
                        ...s,
                        columns: s.columns.map((c, ci) =>
                          ci === 0 ? { ...c, widgets: [...c.widgets, widget] } : c,
                        ),
                      }
                    : s,
                ),
              },
              selection: { kind: "widget", id: widget.id },
            };
          }),

        replaceWidget: (id, next) =>
          set((state) => ({
            page: mapWidgetLists(state.page, (widgets) =>
              widgets.map((w) => (w.id === id ? next : w)),
            ),
            // Keep the new widget selected. If the AI changed the id we
            // follow the change so the inspector refreshes correctly.
            selection: { kind: "widget", id: next.id },
          })),

        insertWidget: (columnId, index, widget) =>
          set((state) => ({
            page: {
              ...state.page,
              sections: state.page.sections.map((s) => ({
                ...s,
                columns: s.columns.map((c) => {
                  if (c.id !== columnId) return c;
                  const clamped = Math.max(0, Math.min(index, c.widgets.length));
                  return {
                    ...c,
                    widgets: [
                      ...c.widgets.slice(0, clamped),
                      widget,
                      ...c.widgets.slice(clamped),
                    ],
                  };
                }),
              })),
            },
            selection: { kind: "widget", id: widget.id },
          })),

        reorderWithinColumn: (columnId, fromIndex, toIndex) =>
          set((state) => {
            if (fromIndex === toIndex) return state;
            return {
              page: {
                ...state.page,
                sections: state.page.sections.map((s) => ({
                  ...s,
                  columns: s.columns.map((c) => {
                    if (c.id !== columnId) return c;
                    if (
                      fromIndex < 0 ||
                      fromIndex >= c.widgets.length ||
                      toIndex < 0 ||
                      toIndex >= c.widgets.length
                    ) {
                      return c;
                    }
                    const next = c.widgets.slice();
                    const [removed] = next.splice(fromIndex, 1);
                    if (!removed) return c;
                    next.splice(toIndex, 0, removed);
                    return { ...c, widgets: next };
                  }),
                })),
              },
            };
          }),

        moveWidgetBetweenColumns: (widgetId, fromColumnId, toColumnId, toIndex) =>
          set((state) => {
            if (fromColumnId === toColumnId) return state;
            let moved: Widget | null = null;
            const afterRemove: Page = {
              ...state.page,
              sections: state.page.sections.map((s) => ({
                ...s,
                columns: s.columns.map((c) => {
                  if (c.id !== fromColumnId) return c;
                  const idx = c.widgets.findIndex((w) => w.id === widgetId);
                  if (idx === -1) return c;
                  const next = c.widgets.slice();
                  const [removed] = next.splice(idx, 1);
                  if (removed) moved = removed;
                  return { ...c, widgets: next };
                }),
              })),
            };
            if (!moved) return state;
            const target = moved as Widget;
            const final: Page = {
              ...afterRemove,
              sections: afterRemove.sections.map((s) => ({
                ...s,
                columns: s.columns.map((c) => {
                  if (c.id !== toColumnId) return c;
                  const clamped = Math.max(0, Math.min(toIndex, c.widgets.length));
                  return {
                    ...c,
                    widgets: [
                      ...c.widgets.slice(0, clamped),
                      target,
                      ...c.widgets.slice(clamped),
                    ],
                  };
                }),
              })),
            };
            return {
              page: final,
              selection: { kind: "widget", id: target.id },
            };
          }),

        // ---------- section ----------

        updateSection: (id, patch) =>
          set((state) => ({
            page: mapSections(state.page, (s) => (s.id === id ? { ...s, ...patch } : s)),
          })),

        addSection: (afterSectionId) =>
          set((state) => {
            const newSection: Section = {
              id: newId("s"),
              columns: [{ id: newId("c"), widgets: [] }],
              padding: { top: 64, right: 24, bottom: 64, left: 24 },
            };
            const { sections } = state.page;
            let next: Section[];
            if (!afterSectionId) {
              next = [...sections, newSection];
            } else {
              const idx = sections.findIndex((s) => s.id === afterSectionId);
              if (idx === -1) next = [...sections, newSection];
              else
                next = [
                  ...sections.slice(0, idx + 1),
                  newSection,
                  ...sections.slice(idx + 1),
                ];
            }
            return {
              page: { ...state.page, sections: next },
              selection: { kind: "section", id: newSection.id },
            };
          }),

        removeSection: (id) =>
          set((state) => {
            if (state.page.sections.length <= 1) return state;
            const wasSelected =
              state.selection?.kind === "section" && state.selection.id === id;
            return {
              page: {
                ...state.page,
                sections: state.page.sections.filter((s) => s.id !== id),
              },
              selection: wasSelected ? null : state.selection,
            };
          }),

        moveSection: (id, direction) =>
          set((state) => {
            const idx = state.page.sections.findIndex((s) => s.id === id);
            if (idx === -1) return state;
            const swap = direction === "up" ? idx - 1 : idx + 1;
            if (swap < 0 || swap >= state.page.sections.length) return state;
            const next = state.page.sections.slice();
            const a = next[idx];
            const b = next[swap];
            if (!a || !b) return state;
            next[idx] = b;
            next[swap] = a;
            return { page: { ...state.page, sections: next } };
          }),

        duplicateSection: (id) =>
          set((state) => {
            const idx = state.page.sections.findIndex((s) => s.id === id);
            if (idx === -1) return state;
            const src = state.page.sections[idx];
            if (!src) return state;
            // Deep-clone with fresh ids so undo/redo history stays coherent
            // and selection doesn't ping-pong between duplicates.
            const clone: Section = {
              ...structuredClone(src),
              id: newId("s"),
              columns: src.columns.map((c) => ({
                ...structuredClone(c),
                id: newId("c"),
                widgets: c.widgets.map((w) => ({
                  ...structuredClone(w),
                  id: newId(w.type),
                })),
              })),
            };
            return {
              page: {
                ...state.page,
                sections: [
                  ...state.page.sections.slice(0, idx + 1),
                  clone,
                  ...state.page.sections.slice(idx + 1),
                ],
              },
              selection: { kind: "section", id: clone.id },
            };
          }),

        // ---------- column ----------

        updateColumn: (id, patch) =>
          set((state) => ({
            page: {
              ...state.page,
              sections: state.page.sections.map((s) => ({
                ...s,
                columns: s.columns.map((c) => (c.id === id ? { ...c, ...patch } : c)),
              })),
            },
          })),

        addColumn: (sectionId, afterColumnId) =>
          set((state) => {
            const newCol: Column = {
              id: newId("c"),
              span: 6,
              widgets: [],
            };
            return {
              page: {
                ...state.page,
                sections: state.page.sections.map((s) => {
                  if (s.id !== sectionId) return s;
                  if (!afterColumnId) return { ...s, columns: [...s.columns, newCol] };
                  const idx = s.columns.findIndex((c) => c.id === afterColumnId);
                  if (idx === -1) return { ...s, columns: [...s.columns, newCol] };
                  return {
                    ...s,
                    columns: [
                      ...s.columns.slice(0, idx + 1),
                      newCol,
                      ...s.columns.slice(idx + 1),
                    ],
                  };
                }),
              },
              selection: { kind: "column", id: newCol.id },
            };
          }),

        removeColumn: (sectionId, columnId) =>
          set((state) => {
            const wasSelected =
              state.selection?.kind === "column" && state.selection.id === columnId;
            return {
              page: {
                ...state.page,
                sections: state.page.sections.map((s) => {
                  if (s.id !== sectionId) return s;
                  if (s.columns.length <= 1) return s;
                  return {
                    ...s,
                    columns: s.columns.filter((c) => c.id !== columnId),
                  };
                }),
              },
              selection: wasSelected ? null : state.selection,
            };
          }),

        moveColumn: (sectionId, columnId, direction) =>
          set((state) => ({
            page: {
              ...state.page,
              sections: state.page.sections.map((s) => {
                if (s.id !== sectionId) return s;
                const idx = s.columns.findIndex((c) => c.id === columnId);
                if (idx === -1) return s;
                const swap = direction === "left" ? idx - 1 : idx + 1;
                if (swap < 0 || swap >= s.columns.length) return s;
                const next = s.columns.slice();
                const a = next[idx];
                const b = next[swap];
                if (!a || !b) return s;
                next[idx] = b;
                next[swap] = a;
                return { ...s, columns: next };
              }),
            },
          })),

        setColumnLayout: (sectionId, spans) =>
          set((state) => {
            if (spans.length === 0) return state;
            return {
              page: {
                ...state.page,
                sections: state.page.sections.map((s) => {
                  if (s.id !== sectionId) return s;
                  const existing = s.columns;
                  const next: Column[] = spans.map((span, i) => {
                    const prev = existing[i];
                    if (prev) return { ...prev, span };
                    return { id: newId("c"), span, widgets: [] };
                  });
                  if (existing.length > spans.length) {
                    const lastIdx = spans.length - 1;
                    const last = next[lastIdx];
                    if (last) {
                      const extraWidgets = existing
                        .slice(spans.length)
                        .flatMap((c) => c.widgets);
                      next[lastIdx] = {
                        ...last,
                        widgets: [...last.widgets, ...extraWidgets],
                      };
                    }
                  }
                  return { ...s, columns: next };
                }),
              },
            };
          }),

        duplicateColumn: (sectionId, columnId) =>
          set((state) => {
            let newColId: string | null = null;
            return {
              page: {
                ...state.page,
                sections: state.page.sections.map((s) => {
                  if (s.id !== sectionId) return s;
                  const idx = s.columns.findIndex((c) => c.id === columnId);
                  if (idx === -1) return s;
                  const src = s.columns[idx];
                  if (!src) return s;
                  const clone: Column = {
                    ...structuredClone(src),
                    id: newId("c"),
                    widgets: src.widgets.map((w) => ({
                      ...structuredClone(w),
                      id: newId(w.type),
                    })),
                  };
                  newColId = clone.id;
                  return {
                    ...s,
                    columns: [
                      ...s.columns.slice(0, idx + 1),
                      clone,
                      ...s.columns.slice(idx + 1),
                    ],
                  };
                }),
              },
              selection: newColId ? { kind: "column", id: newColId } : state.selection,
            };
          }),

        // ---------- smart delete ----------

        deleteSelected: () => {
          const state = get();
          if (!state.selection) return;
          if (state.selection.kind === "widget") {
            state.deleteWidget(state.selection.id);
            return;
          }
          if (state.selection.kind === "section") {
            state.removeSection(state.selection.id);
            return;
          }
          if (state.selection.kind === "column") {
            // Find which section owns this column.
            const section = state.page.sections.find((s) =>
              s.columns.some((c) => c.id === state.selection?.id),
            );
            if (section) state.removeColumn(section.id, state.selection.id);
          }
        },

        resetPage: () => set({ page: BLANK_PAGE, selection: null }),
      }),
      {
        name: PERSIST_KEY,
        version: 1,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ page: state.page }),
        migrate: (persisted, version) => {
          if (version < 1) return { page: BLANK_PAGE };
          return persisted as { page: Page };
        },
      },
    ),
    {
      partialize: (state) => ({ page: state.page }),
      limit: 100,
      equality: (a, b) => a.page === b.page,
    },
  ),
);

export const useEditor = useEditorBase;

export function useEditorHistory() {
  return useStore(useEditorBase.temporal);
}

// ---------- convenience selectors ----------

export const useSelectedWidgetId = (): string | null =>
  useEditor((s) => (s.selection?.kind === "widget" ? s.selection.id : null));
export const useSelectedSectionId = (): string | null =>
  useEditor((s) => (s.selection?.kind === "section" ? s.selection.id : null));
export const useSelectedColumnId = (): string | null =>
  useEditor((s) => (s.selection?.kind === "column" ? s.selection.id : null));

export function isPageBlank(): boolean {
  const p = useEditorBase.getState().page;
  return (
    p.id === BLANK_PAGE.id &&
    p.sections.length === 1 &&
    p.sections[0]?.columns[0]?.widgets.length === 0
  );
}
